import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Box,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import RadioGroup from "@mui/material/RadioGroup";
import Radio from "@mui/material/Radio";
import { toast } from "react-toastify";
import { StyledButton } from "../styled-components";
import Colors from "../../../utils/theme/color";
import {
	useLazyGetCustomerInsightQuestionnaireQuery,
	useSaveCustomerInsightQuestionnaireMutation,
} from "../../../../features/api/opportunity-api-slice";

interface OptionItem {
	value: string;
	label: string;
}

interface QuestionAnswer {
	single: string;
	multi: string[];
	text: string;
	other_data: string;
	other_data_map: Record<string, string>;
}

interface QuestionItem {
	name: string;
	question_text?: string;
	answer_type?: string;
	is_mandatory?: number;
	section_title?: string;
	counts_toward_scope?: string;
	progress_weight?: number;
	other_option_ids?: string[];
	options?: Array<{
		id?: string;
		value?: string;
		label?: string;
		name?: string;
		option_label?: string;
		order?: number;
		is_other?: number;
	}>;
	option_list?: Array<{
		id?: string;
		value?: string;
		label?: string;
		name?: string;
		option_label?: string;
		order?: number;
		is_other?: number;
	}>;
	current?: Partial<QuestionAnswer>;
	max_selections?: number;
	section_intro?: string;
}

interface ScopeProgressItem {
	answered_weight?: number;
	total_weight?: number;
	percent?: number;
	has_questions?: boolean;
}

interface CustomerProfilingModalProps {
	open: boolean;
	onClose: () => void;
	userToken: string;
	opportunityId?: string;
	profileDocname?: string;
	source?: string;
	readOnly?: boolean;
	requestKey?: number;
}

const SOURCES_REQUIRING_PRESALES_AND_SALES = new Set([
	"walk in",
	"existing customer",
	"customer referral",
	"channel partner",
	"employee referral",
	"cp app",
]);

const PAGE_SIZE = 24;
const SANS_FONT_FAMILY = "Arial, Helvetica, sans-serif";

const emptyAnswer = (): QuestionAnswer => ({
	single: "",
	multi: [],
	text: "",
	other_data: "",
	other_data_map: {},
});

const getDraftKey = (opportunityId?: string, profileDocname?: string) =>
	`customer_insight_page_draft::Opportunity::${opportunityId || ""}::${profileDocname || ""}`;

const normalizeOptions = (question: QuestionItem): OptionItem[] => {
	const raw = question.options || question.option_list || [];
	return raw
		.slice()
		.sort((a: any, b: any) => Number(a?.order || 0) - Number(b?.order || 0))
		.map((item: any) => {
			// Support server payload keys: name + option_label
			const value = String(item?.value || item?.id || item?.name || "");
			const label = String(item?.label || item?.option_label || item?.value || item?.id || item?.name || "");
			return { value, label };
		});
};

const isAnswered = (question: QuestionItem, answer: QuestionAnswer): boolean => {
	const answerType = String(question.answer_type || "").toLowerCase();
	const otherOptionIds = new Set((question.other_option_ids || []).map(String));

	if (answerType === "single" || answerType === "dropdown") {
		if (!answer.single) return false;
		if (otherOptionIds.has(answer.single)) return Boolean(answer.other_data?.trim());
		return true;
	}

	if (answerType === "multi") {
		if (!answer.multi.length) return false;
		for (const value of answer.multi) {
			if (otherOptionIds.has(String(value)) && !String(answer.other_data_map?.[value] || "").trim()) {
				return false;
			}
		}
		return true;
	}

	return Boolean(answer.text?.trim());
};

const isQuestionVisibleForScope = (questionScope: string, effectiveScope: string, source?: string): boolean => {
	const normalizedQuestionScope = String(questionScope || "").toLowerCase().trim();
	const normalizedEffectiveScope = String(effectiveScope || "").toLowerCase().trim();
	const normalizedSource = String(source || "").toLowerCase().trim();

	if (!normalizedEffectiveScope) {
		return true;
	}

	if (
		normalizedEffectiveScope === "sales" &&
		SOURCES_REQUIRING_PRESALES_AND_SALES.has(normalizedSource)
	) {
		return normalizedQuestionScope === "presales" || normalizedQuestionScope === "sales";
	}

	if (normalizedEffectiveScope === "sales") {
		return normalizedQuestionScope === "sales" || normalizedQuestionScope === "presales";
	}

	return normalizedQuestionScope === normalizedEffectiveScope;
};

const isQuestionReadOnly = (questionScope: string, effectiveScope: string, source?: string): boolean => {
	return false;
};

const themedFieldSx = {
	"& .MuiInputLabel-root": {
		color: Colors.greyColor,
		fontFamily: SANS_FONT_FAMILY,
		fontSize: 13,
	},
	"& .MuiOutlinedInput-root": {
		backgroundColor: Colors.white,
		borderRadius: "10px",
		fontFamily: SANS_FONT_FAMILY,
		color: Colors.titleColor,
		"& fieldset": {
			borderColor: Colors.veryLightBlack,
		},
		"&:hover fieldset": {
			borderColor: Colors.primaryColor,
		},
		"&.Mui-focused fieldset": {
			borderColor: Colors.primaryColor,
			borderWidth: 1,
		},
	},
};

const optionLabelSx = {
	fontFamily: SANS_FONT_FAMILY,
	fontSize: 15,
	color: Colors.titleColor,
	lineHeight: 1.3,
	wordBreak: "break-word",
};

const CustomerProfilingModal = ({
	open,
	onClose,
	userToken,
	opportunityId,
	profileDocname,
	source,
	readOnly = false,
	requestKey = 0,
}: CustomerProfilingModalProps) => {
	const [questions, setQuestions] = useState<QuestionItem[]>([]);
	const [answersMap, setAnswersMap] = useState<Record<string, QuestionAnswer>>({});
	const [search, setSearch] = useState("");
	const [selectedSection, setSelectedSection] = useState("");
	const [onlyMandatory, setOnlyMandatory] = useState(false);
	const [onlyUnanswered, setOnlyUnanswered] = useState(false);
	const [selectedScopeFilter, setSelectedScopeFilter] = useState("");
	const [effectiveScope, setEffectiveScope] = useState("");
	const [salesScopeProgress, setSalesScopeProgress] = useState<ScopeProgressItem>({});
	const [serverAnswersHash, setServerAnswersHash] = useState("");
	const [pageIndex, setPageIndex] = useState(0);
	const [isSaving, setIsSaving] = useState(false);
	const [isLoadingQuestions, setIsLoadingQuestions] = useState(false);

	const [getQuestionnaire, { isFetching }] = useLazyGetCustomerInsightQuestionnaireQuery();
	const [saveQuestionnaire] = useSaveCustomerInsightQuestionnaireMutation();

	useEffect(() => {
		if (open) return;

		setQuestions([]);
		setAnswersMap({});
		setEffectiveScope("");
		setSalesScopeProgress({});
		setServerAnswersHash("");
		setPageIndex(0);
		setIsLoadingQuestions(false);
	}, [open]);

	const setQuestionAnswer = (questionName: string, updater: (prev: QuestionAnswer) => QuestionAnswer) => {
		setAnswersMap((prev) => ({
			...prev,
			[questionName]: updater(prev[questionName] || emptyAnswer()),
		}));
	};

	const loadQuestions = useCallback(async () => {
		if (!open || !opportunityId) return;

		setIsLoadingQuestions(true);
		setQuestions([]);
		setAnswersMap({});
		setSalesScopeProgress({});
		try {
			const response: any = await getQuestionnaire({
				userToken,
				docname: profileDocname || "",
				contextDoctype: "Opportunity",
				contextMode: readOnly ? "view" : "edit",
				contextDocname: opportunityId,
				requestKey,
			}, false).unwrap();

			const message = response?.message || response?.data || {};
			const nextQuestions: QuestionItem[] = Array.isArray(message?.questions) ? message.questions : [];
			const nextEffectiveScope = String(message?.effective_scope || "").toLowerCase();
			const nextSalesScopeProgress = message?.scope_progress?.sales || {};
			const nextHash = String(message?.answers_hash || "");

			const nextAnswers: Record<string, QuestionAnswer> = {};
			nextQuestions.forEach((q) => {
				const cur = q.current || {};
				nextAnswers[q.name] = {
					single: String(cur.single || ""),
					multi: Array.isArray(cur.multi) ? cur.multi.map(String) : [],
					text: String(cur.text || ""),
					other_data: String(cur.other_data || ""),
					other_data_map: cur.other_data_map || {},
				};
			});

			setQuestions(nextQuestions);
			setAnswersMap(nextAnswers);
			setEffectiveScope(nextEffectiveScope);
			setSalesScopeProgress(nextSalesScopeProgress);
			setServerAnswersHash(nextHash);
			setPageIndex(0);

			const draftRaw = localStorage.getItem(getDraftKey(opportunityId, profileDocname));
			if (draftRaw) {
				const draft = JSON.parse(draftRaw);
				if (Array.isArray(draft?.payload) && String(draft?.answers_hash || "") === nextHash) {
					const draftAnswers = { ...nextAnswers };
					draft.payload.forEach((item: any) => {
						const key = String(item.question || "");
						if (!key) return;
						draftAnswers[key] = {
							single: String(item.single || ""),
							multi: Array.isArray(item.multi) ? item.multi.map(String) : [],
							text: String(item.text || ""),
							other_data: String(item.other_data || ""),
							other_data_map: item.other_data_map || {},
						};
					});
					setAnswersMap(draftAnswers);
					toast.info("Local draft restored");
				}
			}
		} catch (error) {
			setQuestions([]);
			setAnswersMap({});
			setSalesScopeProgress({});
			toast.error("Failed to load customer profiling questions");
		} finally {
			setIsLoadingQuestions(false);
		}
	}, [
		getQuestionnaire,
		open,
		opportunityId,
		profileDocname,
		readOnly,
		requestKey,
		userToken,
	]);

	useEffect(() => {
		loadQuestions();
	}, [loadQuestions]);

	useEffect(() => {
		if (!open || !opportunityId || readOnly) return;
		const timer = setTimeout(() => {
			const payload = questions.map((q) => {
				const answer = answersMap[q.name] || emptyAnswer();
				return {
					question: q.name,
					answer_type: q.answer_type,
					single: answer.single,
					multi: answer.multi,
					text: answer.text,
					other_data: answer.other_data,
					other_data_map: answer.other_data_map,
				};
			});

			localStorage.setItem(
				getDraftKey(opportunityId, profileDocname),
				JSON.stringify({
					saved_at: new Date().toISOString(),
					answers_hash: serverAnswersHash,
					payload,
				})
			);
		}, 300);

		return () => clearTimeout(timer);
	}, [answersMap, questions, open, opportunityId, profileDocname, readOnly, serverAnswersHash]);

	const sections = useMemo(() => {
		return Array.from(new Set(questions.map((q) => String(q.section_title || "").trim()).filter(Boolean))).sort();
	}, [questions]);

	const visibleQuestions = useMemo(() => {
		const term = search.toLowerCase().trim();

		return questions.filter((q) => {
			const questionText = `${q.question_text || ""} ${q.name || ""}`.toLowerCase();
			const scope = String(q.counts_toward_scope || "").toLowerCase().trim();
			const answer = answersMap[q.name] || emptyAnswer();
			const readOnly = isQuestionReadOnly(scope, effectiveScope, source);

			if (!isQuestionVisibleForScope(scope, effectiveScope, source)) return false;
			if (readOnly && !isAnswered(q, answer)) return false;
			if (term && !questionText.includes(term)) return false;
			if (selectedSection && String(q.section_title || "") !== selectedSection) return false;
			if (selectedScopeFilter && scope !== selectedScopeFilter) return false;
			if (onlyMandatory && Number(q.is_mandatory || 0) !== 1) return false;
			if (onlyUnanswered && isAnswered(q, answer)) return false;
			return true;
		});
	}, [
		answersMap,
		effectiveScope,
		onlyMandatory,
		onlyUnanswered,
		questions,
		search,
		selectedScopeFilter,
		selectedSection,
		source,
	]);

	const totalPages = Math.max(1, Math.ceil(visibleQuestions.length / PAGE_SIZE));

	const pagedQuestions = useMemo(() => {
		const start = pageIndex * PAGE_SIZE;
		return visibleQuestions.slice(start, start + PAGE_SIZE);
	}, [pageIndex, visibleQuestions]);

	const progressSummary = useMemo(() => {
		const percent = Number(salesScopeProgress?.percent || 0);
		const answered = questions.reduce((acc, q) => {
			const a = answersMap[q.name] || emptyAnswer();
			return acc + (isAnswered(q, a) ? 1 : 0);
		}, 0);

		return {
			answered,
			total: questions.length,
			percent: Math.round(percent),
		};
	}, [answersMap, questions, salesScopeProgress]);

	const onSave = async () => {
		if (readOnly) return;
		if (!opportunityId) return;

		setIsSaving(true);
		try {
			const payload = questions.map((q) => {
				const answer = answersMap[q.name] || emptyAnswer();
				return {
					question: q.name,
					answer_type: q.answer_type,
					single: answer.single,
					multi: answer.multi,
					text: answer.text,
					other_data: answer.other_data,
					other_data_map: answer.other_data_map,
				};
			});

			const response: any = await saveQuestionnaire({
				userToken,
				payload: {
					docname: profileDocname || "",
					title: "Opportunity Customer Insight",
					payload_json: JSON.stringify(payload),
					context_doctype: "Opportunity",
					context_mode: "edit",
					context_docname: opportunityId,
					expected_answers_hash: serverAnswersHash,
				},
			}).unwrap();

			const message = response?.message || response?.data || response;
			if (message?.ok || response?.status_code === 200) {
				localStorage.removeItem(getDraftKey(opportunityId, profileDocname));
				toast.success("Customer profiling saved");
				await loadQuestions();
				return;
			}

			toast.error(message?.message || "Unable to save customer profiling");
		} catch (error: any) {
			const text = JSON.stringify(error || {});
			if (text.includes("TimestampMismatchError") || text.includes("Saved answers on the server")) {
				toast.warning("Answers changed on server. Latest data has been reloaded.");
				await loadQuestions();
			} else {
				toast.error("Please fill out the mandatory fields before saving");
			}
		} finally {
			setIsSaving(false);
		}
	};

	const renderQuestionInput = (question: QuestionItem) => {
		const answerType = String(question.answer_type || "").toLowerCase();
		const answer = answersMap[question.name] || emptyAnswer();
		const options = normalizeOptions(question);
		const otherOptionIds = new Set((question.other_option_ids || []).map(String));
		const questionScope = String(question.counts_toward_scope || "").toLowerCase().trim();
		const isReadOnlyInput = readOnly || isQuestionReadOnly(questionScope, effectiveScope, source);
		const optionLabelMap = new Map(options.map((option) => [option.value, option.label]));

		if (isReadOnlyInput) {
			let displayValue = "";

			if (answerType === "single" || answerType === "dropdown") {
				const selectedLabel = optionLabelMap.get(answer.single) || answer.single;
				displayValue = answer.other_data?.trim()
					? `${selectedLabel}: ${answer.other_data.trim()}`
					: selectedLabel;
			} else if (answerType === "multi") {
				displayValue = answer.multi
					.map((value) => {
						const selectedLabel = optionLabelMap.get(value) || value;
						const otherValue = String(answer.other_data_map?.[value] || "").trim();
						return otherValue ? `${selectedLabel}: ${otherValue}` : selectedLabel;
					})
					.join(", ");
			} else {
				displayValue = answer.text;
			}

			return (
				<Box
					sx={{
						backgroundColor: Colors.cyanBlueColor,
						border: `1px solid ${Colors.lightBlueColor}`,
						borderRadius: "12px",
						px: 1.75,
						py: 1.5,
					}}
				>
					<Typography
						sx={{
							fontFamily: SANS_FONT_FAMILY,
							fontSize: 14,
							color: Colors.titleColor,
							lineHeight: 1.5,
						}}
					>
						{displayValue || "-"}
					</Typography>
				</Box>
			);
		}

		if (answerType === "single" || answerType === "dropdown") {
			// Render radio buttons in a row for single selection
			return (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					<FormControl component="fieldset" fullWidth size="small" sx={themedFieldSx}>
						<RadioGroup
							row
							value={answer.single}
							onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
								setQuestionAnswer(question.name, (prev) => ({
									...prev,
									single: String(e.target.value || ""),
								}))
							}
						>
							{options.map((option) => (
								<FormControlLabel
									key={option.value}
									value={option.value}
									control={<Radio sx={{ color: Colors.greyColor, '&.Mui-checked': { color: Colors.primaryColor } }} />}
									label={option.label}
									sx={{ mr: 3, ...optionLabelSx }}
									componentsProps={{
										typography: { sx: optionLabelSx },
									}}
								/>
							))}
						</RadioGroup>
					</FormControl>
					{otherOptionIds.has(answer.single) && (
						<TextField
							size="small"
							fullWidth
							label="Please specify"
							sx={themedFieldSx}
							value={answer.other_data}
							onChange={(e) =>
								setQuestionAnswer(question.name, (prev) => ({
									...prev,
									other_data: e.target.value,
								}))
							}
						/>
					)}
				</Box>
			);
		}

		if (answerType === "multi") {
			const maxSelections = question.max_selections;
			const selectedCount = answer.multi.length;
			return (
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
					{maxSelections && (
						<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 13, color: Colors.greyColor, mb: 1 }}>
							Select at most {maxSelections} option{maxSelections > 1 ? 's' : ''}
						</Typography>
					)}
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
							gap: 1.5,
							alignItems: "flex-start",
						}}
					>
						{options.map((option) => {
							const checked = answer.multi.includes(option.value);
							const disabled =
								!!maxSelections && !checked && selectedCount >= maxSelections;
							return (
								<Box
									key={option.value}
									sx={{
										backgroundColor: Colors.transparentColor,
										border: "none",
										px: 0,
										py: 0,
									}}
								>
									<FormControlLabel
										sx={{ width: "100%", gap: 0.5, ...optionLabelSx }}
										control={
											<Checkbox
												sx={{
													alignSelf: "flex-start",
													padding: "2px 9px 0 0",
													color: Colors.greyColor,
													"&.Mui-checked": {
														color: Colors.primaryColor,
													},
												}}
												checked={checked}
												disabled={disabled}
												onChange={(e) => {
													const isChecked = e.target.checked;
													setQuestionAnswer(question.name, (prev) => ({
														...prev,
														multi: isChecked
															? [...prev.multi, option.value]
															: prev.multi.filter((value) => value !== option.value),
													}));
												}}
											/>
										}
										label={option.label}
										componentsProps={{
											typography: { sx: optionLabelSx },
										}}
									/>
									{checked && otherOptionIds.has(option.value) && (
										<TextField
											size="small"
											fullWidth
											label="Please specify"
											sx={{ ...themedFieldSx, mt: 0.5 }}
											value={answer.other_data_map?.[option.value] || ""}
											onChange={(e) =>
												setQuestionAnswer(question.name, (prev) => ({
													...prev,
													other_data_map: {
														...prev.other_data_map,
														[option.value]: e.target.value,
													},
												}))
											}
										/>
									)}
								</Box>
							);
						})}
					</Box>
				</Box>
			);
		}

		return (
			<TextField
				fullWidth
				multiline
				minRows={2}
				size="small"
				label="Your answer"
				sx={themedFieldSx}
				value={answer.text}
				onChange={(e) =>
					setQuestionAnswer(question.name, (prev) => ({
						...prev,
						text: e.target.value,
					}))
				}
			/>
		);
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="lg"
			PaperProps={{
				sx: {
					borderRadius: "18px",
					overflow: "hidden",
					backgroundColor: Colors.whiteSmoke,
					boxShadow: "0px 20px 50px rgba(13, 77, 161, 0.16)",
				},
			}}
		>
			<DialogTitle
				sx={{
					px: { xs: 2, sm: 3 },
					py: 2.5,
					background: `linear-gradient(135deg, ${Colors.white} 0%, ${Colors.cyanBlueColor} 100%)`,
					borderBottom: `1px solid ${Colors.lightBlueColor}`,
				}}
			>
				<Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2 }}>
					<Box>
						<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 24, color: Colors.primaryColor, lineHeight: 1.2, fontWeight: 700 }}>
							{`Customer Profiling ${progressSummary.percent}%`}
						</Typography>
						<Typography sx={{ mt: 0.5, fontFamily: SANS_FONT_FAMILY, fontSize: 13, color: Colors.titleColor }}>
							Capture buyer preferences for more personalized recommendations.
						</Typography>
					</Box>
					<Box
						sx={{
							minWidth: 132,
							backgroundColor: Colors.white,
							border: `1px solid ${Colors.lightBlueColor}`,
							borderRadius: "14px",
							px: 1.5,
							py: 1,
							boxShadow: "0px 8px 20px rgba(13, 77, 161, 0.08)",
						}}
					>
						<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 11, color: Colors.greyColor, textTransform: "uppercase", letterSpacing: 0.8 }}>
							Sales Progress
						</Typography>
						<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 18, color: Colors.primaryColor, fontWeight: 700 }}>
							{progressSummary.percent}%
						</Typography>
						<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 12, color: Colors.titleColor }}>
							{progressSummary.answered}/{progressSummary.total} answered
						</Typography>
					</Box>
				</Box>
				<Box
					sx={{
						mt: 2,
						height: 8,
						borderRadius: 999,
						backgroundColor: Colors.lightBlueColor,
						overflow: "hidden",
					}}
				>
					<Box
						sx={{
							width: `${progressSummary.percent}%`,
							height: "100%",
							borderRadius: 999,
							background: `linear-gradient(90deg, ${Colors.actionColor} 0%, ${Colors.primaryColor} 100%)`,
							transition: "width 0.2s ease",
						}}
					/>
				</Box>
			</DialogTitle>
			<DialogContent sx={{ px: { xs: 2, sm: 3 }, py: 3, backgroundColor: Colors.whiteSmoke }}>
				<Box
					sx={{
						mb: 2,
						p: 1.5,
						borderRadius: "16px",
						backgroundColor: Colors.white,
						border: `1px solid ${Colors.veryLightBlack}`,
						boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.05)",
					}}
				>
					<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 14, color: Colors.titleColor, fontWeight: 700 }}>
						Questionnaire Overview
					</Typography>
					<Typography sx={{ mt: 0.5, fontFamily: SANS_FONT_FAMILY, fontSize: 13, color: Colors.greyColor }}>
						Review by section, search quickly, and complete all mandatory responses before saving.
					</Typography>
				</Box>

				<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
					{effectiveScope ? (
						<StyledButton
							onClick={() =>
								setSelectedScopeFilter((prev) => (prev === effectiveScope ? "" : effectiveScope))
							}
							sx={{
								pt: 0.6,
								pb: 0.6,
								px: 1.5,
								border: `1px solid ${selectedScopeFilter === effectiveScope ? Colors.primaryColor : Colors.veryLightBlack}`,
								background: selectedScopeFilter === effectiveScope ? Colors.veryLightPrimaryColor : Colors.white,
								color: selectedScopeFilter === effectiveScope ? Colors.primaryColor : Colors.titleColor,
							}}
						>
							{effectiveScope.toUpperCase()} Scope
						</StyledButton>
					) : null}
				</Box>

				<Box
					sx={{
						display: "flex",
						flexWrap: "wrap",
						gap: 1,
						mb: 2,
						p: 1.5,
						borderRadius: "16px",
						backgroundColor: Colors.white,
						border: `1px solid ${Colors.veryLightBlack}`,
						boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.05)",
					}}
				>
					<TextField
						size="small"
						label="Search questions"
						sx={{ ...themedFieldSx, minWidth: { xs: "100%", md: 260 }, flex: 1 }}
						value={search}
						onChange={(e) => {
							setSearch(e.target.value);
							setPageIndex(0);
						}}
					/>
					<FormControl size="small" sx={{ ...themedFieldSx, minWidth: 180 }}>
						<InputLabel>Section</InputLabel>
						<Select
							value={selectedSection}
							label="Section"
							onChange={(e) => {
								setSelectedSection(String(e.target.value || ""));
								setPageIndex(0);
							}}
						>
							<MenuItem value="">All Sections</MenuItem>
							{sections.map((section) => (
								<MenuItem key={section} value={section}>
									{section}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControlLabel
						sx={{
							ml: 0.25,
							px: 1,
							borderRadius: "12px",
							backgroundColor: onlyMandatory ? Colors.cyanBlueColor : Colors.transparentColor,
							border: `1px solid ${onlyMandatory ? Colors.lightBlueColor : Colors.transparentColor}`,
							"& .MuiFormControlLabel-label": {
								fontFamily: SANS_FONT_FAMILY,
								fontSize: 14,
								color: Colors.titleColor,
							},
						}}
						control={
							<Checkbox
								sx={{
									color: Colors.greyColor,
									"&.Mui-checked": {
										color: Colors.primaryColor,
									},
								}}
								checked={onlyMandatory}
								onChange={(e) => {
									setOnlyMandatory(e.target.checked);
									setPageIndex(0);
								}}
							/>
						}
						label="Mandatory"
					/>
					<FormControlLabel
						sx={{
							ml: 0.25,
							px: 1,
							borderRadius: "12px",
							backgroundColor: onlyUnanswered ? Colors.cyanBlueColor : Colors.transparentColor,
							border: `1px solid ${onlyUnanswered ? Colors.lightBlueColor : Colors.transparentColor}`,
							"& .MuiFormControlLabel-label": {
								fontFamily: SANS_FONT_FAMILY,
								fontSize: 14,
								color: Colors.titleColor,
							},
						}}
						control={
							<Checkbox
								sx={{
									color: Colors.greyColor,
									"&.Mui-checked": {
										color: Colors.primaryColor,
									},
								}}
								checked={onlyUnanswered}
								onChange={(e) => {
									setOnlyUnanswered(e.target.checked);
									setPageIndex(0);
								}}
							/>
						}
						label="Unanswered"
					/>
				</Box>

				{isLoadingQuestions || isFetching ? (
					<Box
						sx={{
							display: "flex",
							justifyContent: "center",
							p: 5,
							borderRadius: "16px",
							backgroundColor: Colors.white,
							border: `1px solid ${Colors.veryLightBlack}`,
						}}
					>
						<CircularProgress size={28} />
					</Box>
				) : (
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{pagedQuestions.length ? (() => {
							// Group questions by section
							let lastSection: string | null = null;
							let questionIndex = 0;
							return pagedQuestions.map((question, idx) => {
								const section = question.section_title || "";
								let sectionHeading = null;
								let sectionIntro = null;
								if (section && section !== lastSection) {
									sectionHeading = (
										<Typography key={`section-${section}-${idx}`}
											sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 22, color: Colors.primaryColor, mb: 0.5, mt: idx === 0 ? 0 : 2, fontWeight: 700 }}
										>
											{section}
										</Typography>
									);
									// Find the first question in this section with section_intro
									const introText = question.section_intro;
									if (introText) {
										sectionIntro = (
											<Typography key={`section-intro-${section}-${idx}`}
												sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 15, color: Colors.greyColor, mb: 1.5 }}
											>
												{introText}
											</Typography>
										);
									}
									lastSection = section;
								}
								// Continuous question numbering across all sections
								const displayQuestionNumber = pageIndex * PAGE_SIZE + idx + 1;
								return (
									<React.Fragment key={question.name}>
										{sectionHeading}
										{sectionIntro}
										<Box
											sx={{
												border: `1px solid ${Colors.veryLightBlack}`,
												borderRadius: "16px",
												p: 1.75,
												backgroundColor: Colors.white,
												boxShadow: "0px 10px 24px rgba(0, 0, 0, 0.05)",
											}}
										>
											<Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, alignItems: "flex-start", mb: 1.25 }}>
												<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 18, color: Colors.titleColor, lineHeight: 1.4, fontWeight: 700 }}>
													{`${displayQuestionNumber}. ${question.question_text || question.name}`}
													{Number(question.is_mandatory || 0) === 1 ? " *" : ""}
												</Typography>
												<Box
													sx={{
														flexShrink: 0,
														px: 1,
														py: 0.4,
														borderRadius: "999px",
														backgroundColor:
															String(question.counts_toward_scope || "").toLowerCase().trim() === "sales"
																? Colors.lightBlueColor
																: Colors.metallicYellow,
													}}
												>
													<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 11, color: Colors.primaryColor, textTransform: "capitalize", fontWeight: 700 }}>
														{String(question.counts_toward_scope || effectiveScope || "general")}
													</Typography>
												</Box>
											</Box>
											{renderQuestionInput(question)}
										</Box>
									</React.Fragment>
								);
							});
						})() : (
							<Box
								sx={{
									p: 4,
									textAlign: "center",
									borderRadius: "16px",
									backgroundColor: Colors.white,
									border: `1px dashed ${Colors.greyColor}`,
								}}
							>
								<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 18, color: Colors.titleColor, fontWeight: 700 }}>
									No questions match your filters.
								</Typography>
								<Typography sx={{ mt: 0.5, fontFamily: SANS_FONT_FAMILY, fontSize: 13, color: Colors.greyColor }}>
									Clear the current filters or search with a broader keyword.
								</Typography>
							</Box>
						)}
					</Box>
				)}

				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mt: 2,
						p: 1.5,
						borderRadius: "16px",
						backgroundColor: Colors.white,
						border: `1px solid ${Colors.veryLightBlack}`,
					}}
				>
					<Typography sx={{ fontFamily: SANS_FONT_FAMILY, fontSize: 12, color: Colors.greyColor }}>
						Showing {visibleQuestions.length ? pageIndex * PAGE_SIZE + 1 : 0}-
						{Math.min((pageIndex + 1) * PAGE_SIZE, visibleQuestions.length)} of {visibleQuestions.length}
					</Typography>
					<Box sx={{ display: "flex", gap: 1 }}>
						<StyledButton
							disabled={pageIndex <= 0}
							onClick={() => setPageIndex((prev) => Math.max(0, prev - 1))}
						>
							Prev
						</StyledButton>
						<StyledButton
							disabled={pageIndex >= totalPages - 1}
							onClick={() => setPageIndex((prev) => Math.min(totalPages - 1, prev + 1))}
						>
							Next
						</StyledButton>
					</Box>
				</Box>
			</DialogContent>
			<DialogActions
				sx={{
					px: { xs: 2, sm: 3 },
					py: 2,
					backgroundColor: Colors.white,
					borderTop: `1px solid ${Colors.veryLightBlack}`,
				}}
			>
				<StyledButton onClick={onClose}>Close</StyledButton>
				{!readOnly && (
					<StyledButton
						onClick={onSave}
						disabled={isSaving || isFetching}
						sx={{
							backgroundColor: Colors.actionColor,
							border: `1px solid ${Colors.actionColor}`,
							color: Colors.primaryColor,
							"&:hover": {
								backgroundColor: Colors.goldenPoppy,
								border: `1px solid ${Colors.goldenPoppy}`,
							},
						}}
					>
						{isSaving ? <CircularProgress size={18} color="secondary" /> : "Save Answers"}
					</StyledButton>
				)}
			</DialogActions>
		</Dialog>
	);
};

export default CustomerProfilingModal;
